(() => {
  const API_KEY = 'StationStickyAtc';
  const REGISTRY_KEY = '__stationStickyAtcRegistry';
  const MOBILE_BREAKPOINT_QUERY = '(max-width: 749px)';

  function findPrice(scope, sectionId) {
    if (!scope) return null;
    return (
      scope.querySelector(`#price-${sectionId} .price-item--sale`) ||
      scope.querySelector(`#price-${sectionId} .price-item--regular`) ||
      scope.querySelector(`#price-${sectionId} .price-item`)
    );
  }

  function initBar(bar, registry) {
    const sectionId = String(bar?.dataset?.sectionId || '');
    if (!sectionId) return;

    const existing = registry.get(sectionId);
    if (existing?.bar === bar) return;
    if (existing?.cleanup) existing.cleanup();

    const root = document.getElementById(`MainProduct-${sectionId}`);
    const trigger = root?.querySelector(`#ProductSubmitButton-${sectionId}`);
    if (!root || !trigger) return;

    const mobileQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
    const priceEl = bar.querySelector('[data-sticky-atc-price]');
    const button = bar.querySelector('[data-sticky-atc-button]');
    const dismissButton = bar.querySelector('[data-sticky-atc-dismiss]');
    const buttonTextEl = bar.querySelector('[data-sticky-atc-button-text]');

    let visible = false;
    let dismissed = false;
    let triggerVisible = true;
    let isScrollingDown = true;
    let lastScrollY = window.scrollY || window.pageYOffset || 0;
    let variantUnsubscribe = null;
    let observer = null;

    function setVisible(nextVisible) {
      if (dismissed || !mobileQuery.matches) nextVisible = false;
      if (visible === nextVisible) return;
      visible = nextVisible;
      bar.classList.toggle('is-visible', visible);
      if (visible) {
        bar.setAttribute('aria-hidden', 'false');
        bar.removeAttribute('inert');
        button?.removeAttribute('tabindex');
        dismissButton?.removeAttribute('tabindex');
        return;
      }
      bar.setAttribute('aria-hidden', 'true');
      bar.setAttribute('inert', '');
      button?.setAttribute('tabindex', '-1');
      dismissButton?.setAttribute('tabindex', '-1');
    }

    function syncFromDom() {
      const sourceButtonText = trigger.querySelector('span');
      if (buttonTextEl && sourceButtonText) {
        buttonTextEl.textContent = sourceButtonText.textContent.trim();
      }

      if (button) {
        if (trigger.hasAttribute('disabled')) button.setAttribute('disabled', 'disabled');
        else button.removeAttribute('disabled');
      }

      const sourcePrice = findPrice(root, sectionId);
      if (priceEl && sourcePrice) {
        priceEl.textContent = sourcePrice.textContent.trim();
      }
    }

    function applyVariantChange(data) {
      if (!data || String(data.sectionId) !== sectionId) return;

      const variantStrings = window.variantStrings || {};
      const htmlRoot = data.html;
      const nextPrice = findPrice(htmlRoot, sectionId);
      if (priceEl && nextPrice) {
        priceEl.textContent = nextPrice.textContent.trim();
      }

      if (buttonTextEl) {
        if (!data.variant) buttonTextEl.textContent = variantStrings.unavailable || buttonTextEl.textContent;
        else if (data.variant.available) buttonTextEl.textContent = variantStrings.addToCart || buttonTextEl.textContent;
        else buttonTextEl.textContent = variantStrings.soldOut || buttonTextEl.textContent;
      }

      if (button) {
        if (data.variant && data.variant.available) button.removeAttribute('disabled');
        else button.setAttribute('disabled', 'disabled');
      }
    }

    function evaluateTriggerVisibility() {
      const rect = trigger.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      return rect.bottom > 0 && rect.top < viewportHeight;
    }

    function refresh() {
      triggerVisible = evaluateTriggerVisibility();
      if (triggerVisible) dismissed = false;
      setVisible(!triggerVisible && isScrollingDown);
      syncFromDom();
    }

    function onScroll() {
      const nextY = window.scrollY || window.pageYOffset || 0;
      isScrollingDown = nextY > lastScrollY;
      lastScrollY = nextY;
      if (!triggerVisible) setVisible(isScrollingDown);
    }

    function onMobileQueryChange() {
      dismissed = false;
      refresh();
    }

    function onDismiss() {
      dismissed = true;
      setVisible(false);
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          if (!entries.length) return;
          triggerVisible = entries[0].isIntersecting;
          if (triggerVisible) dismissed = false;
          setVisible(!triggerVisible && isScrollingDown);
        },
        { threshold: 0.25 }
      );
      observer.observe(trigger);
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        try {
          applyVariantChange(event?.data);
        } catch (error) {}
      });
    }

    dismissButton?.addEventListener('click', onDismiss);
    window.addEventListener('scroll', onScroll, { passive: true });
    if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', onMobileQueryChange);
    else if (mobileQuery.addListener) mobileQuery.addListener(onMobileQueryChange);

    refresh();

    registry.set(sectionId, {
      bar,
      cleanup: () => {
        dismissButton?.removeEventListener('click', onDismiss);
        window.removeEventListener('scroll', onScroll);
        if (mobileQuery.removeEventListener) mobileQuery.removeEventListener('change', onMobileQueryChange);
        else if (mobileQuery.removeListener) mobileQuery.removeListener(onMobileQueryChange);
        observer?.disconnect();
        if (typeof variantUnsubscribe === 'function') variantUnsubscribe();
      },
    });
  }

  function install(api) {
    if (api._installed) return api;
    api._installed = true;
    api.registry = window[REGISTRY_KEY] || new Map();
    window[REGISTRY_KEY] = api.registry;

    api.initAll = () => {
      for (const [sectionId, entry] of api.registry.entries()) {
        if (!entry?.bar || !entry.bar.isConnected) {
          entry?.cleanup?.();
          api.registry.delete(sectionId);
        }
      }

      document
        .querySelectorAll('[data-station-sticky-atc][data-section-id]')
        .forEach((bar) => initBar(bar, api.registry));
    };

    return api;
  }

  const api = install(window[API_KEY] || {});
  window[API_KEY] = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => api.initAll(), { once: true });
  } else {
    api.initAll();
  }
})();
